import { OF, type QueryValue } from '../../ofApi'

/*
 * onlyfans.com/my/release-forms as its bundle builds it (captured 2026-10-22:
 * ReleaseFormsPage + the releaseForms store module + the release-form API
 * module). Requests, query order and bodies are OnlyFans' own.
 */

export type Raw = Record<string, unknown>

export const REFERER = `${OF}/my/release-forms`

/** The page's tabs (OnlyFans' `type`): TAG USER, RELEASE FORM, MENTIONS. */
export type RfTab = 'users' | 'forms' | 'mentions'
/** The list tabs (search and the right-hand widgets know these two only). */
export type RfListTab = Exclude<RfTab, 'mentions'>

/** OnlyFans' default page size (api `Jg`). */
export const DEFAULT_LIMIT = 10
/** Users load 50 at a time (20 while searching by name). */
export const USERS_LIMIT = 50
export const USERS_SEARCH_LIMIT = 20
/** At most this many rows can be selected (`LIMIT_EXCEEDED_MAX_VALUE`). */
export const SELECT_MAX = 50
/** Name inputs of the modals (InputTextField maxlength/counter). */
export const NAME_MAX = 60
/** The search field's maxlength and how many characters start a search. */
export const SEARCH_MAX = 100
export const SEARCH_MIN = 3

export type FilterValue = 'all' | 'pending'
export type SortValue = 'date' | 'name'
export type OrderValue = 'asc' | 'desc'

export interface RfOptions {
  filter: FilterValue
  sort: SortValue
  order: OrderValue
}

export const DEFAULT_OPTIONS: RfOptions = { filter: 'all', sort: 'date', order: 'desc' }

/** The sort button lights up when anything differs from All / Recent / Descending. */
export const isFiltered = (o: RfOptions) => o.filter !== 'all' || o.sort !== 'date' || o.order !== 'desc'

/** Picking Name sorts A→Z, picking Recent newest first (ReleaseFormsFilter.onRadioChanged). */
export function pickOption(o: RfOptions, group: keyof RfOptions, value: string): RfOptions {
  const next = { ...o, [group]: value } as RfOptions
  if (group === 'sort') next.order = value === 'name' ? 'asc' : 'desc'
  return next
}

// ── Requests ────────────────────────────────────────────────────────────────

export interface PageArgs {
  limit: number
  offset: number
  options: RfOptions
  name?: string
  source?: string
}

/** GET posts/tagged-friend-users (fetchUsersPage). */
export function usersQuery({ limit, offset, options, name = '', source = '' }: PageArgs): Record<string, QueryValue> {
  return { limit, offset, skip_users: 'all', filter: options.filter, sort: `${options.sort}:${options.order}`, name, source }
}

/** GET release-forms (fetchFormsPage). */
export function formsQuery({ limit, offset, options, name = '', source = '' }: PageArgs): Record<string, QueryValue> {
  return { limit, offset, filter: options.filter, sort: `${options.sort}:${options.order}`, name, source }
}

/** GET release-forms/mentions (getMentions). */
export function mentionsQuery(offset: number, fromId: string | number | undefined, limit = DEFAULT_LIMIT): Record<string, QueryValue> {
  return { limit, related_user: '', offset, fromId: fromId ?? '', skip_users: 'all', format: 'infinite' }
}

export const LIST_PATH: Record<RfListTab, string> = { users: 'posts/tagged-friend-users', forms: 'release-forms' }
export const MENTIONS_PATH = 'release-forms/mentions'
export const TOGGLE_SHOW_PATH = 'release-forms/toggle-show'
export const RENAME_PATH = 'release-forms/rename'
export const LINKS_PATH = 'release-form-links'
export const GUESTS_PATH = 'guests'

/** PATCH release-forms/toggle-show (hideReleaseForms). */
export const toggleShowBody = (item: Pick<RfItem, 'id' | 'type'>) => ({ entityId: item.id, entityType: item.type })
/** PATCH release-forms/rename (renameReleaseForms). */
export const renameBody = (id: RfItem['id'], name: string) => ({ entityId: id, name })
/** POST release-form-links (saveReleaseLink). */
export const linkBody = (name: string) => ({ name })
/** POST guests, or PATCH guests/<id> when editing (saveGuest). */
export const guestRequest = (name: string, id?: RfItem['id']) => ({
  method: (id ? 'PATCH' : 'POST') as 'PATCH' | 'POST',
  path: id ? `${GUESTS_PATH}/${id}` : `${GUESTS_PATH}/`,
  body: { name },
})

// ── Rows ────────────────────────────────────────────────────────────────────

export interface RfUser {
  id: number
  name: string
  username: string
  avatar: string
  isIdentityVerified: boolean
  hidden: boolean
}

/** One row of TAG USER (a user or a guest invitation) or RELEASE FORM (a link or a document). */
export interface RfItem {
  id: number | string
  /** OnlyFans' entity type: user | guest | link | document. */
  type: string
  name: string
  /** Guest invitations carry a token. */
  token: string
  status: string
  /** Creation date (UTC). */
  date: string
  approveDate: string
  /** The creator who opened/signed the link. */
  userName: string
  code: string
  signature: string
  hasUser: boolean
  hidden: boolean
  user: RfUser | null
}

const str = (v: unknown) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {})

function thumb(u: Raw): string {
  const t = obj(u.avatarThumbs)
  return str(t.c144) || str(t.c50) || str(u.avatar)
}

export function toUser(raw: unknown): RfUser | null {
  const u = obj(raw)
  const inner = obj(u.user)
  const src = inner.id ? inner : u
  if (!src.id) return null
  return {
    id: Number(src.id),
    name: str(src.name),
    username: str(src.username),
    avatar: thumb(src),
    isIdentityVerified: src.isIdentityVerified === true,
    hidden: src.hiddenForRf === true,
  }
}

/** A list row from either list (enrichReleaseForm: the embedded user is the creator). */
export function toItem(raw: unknown, fallbackType?: string): RfItem | null {
  const r = obj(raw)
  if (r.id === undefined || r.id === null || r.id === '') return null
  const token = str(r.token)
  return {
    id: typeof r.id === 'number' ? r.id : str(r.id),
    type: str(r.type) || fallbackType || (token ? 'guest' : 'user'),
    name: decodeEntities(str(r.name)),
    token,
    status: str(r.status),
    date: str(r.date),
    approveDate: str(r.approve_date),
    userName: str(r.user_name),
    code: str(r.code),
    signature: str(r.signature),
    hasUser: r.hasUser === true,
    hidden: r.hiddenForRf === true,
    user: toUser(r.user),
  }
}

/** `{items, hasMore}` of either list. */
export function parsePage(data: unknown): { items: RfItem[]; hasMore: boolean } {
  const d = obj(data)
  const list = Array.isArray(d.items) ? d.items : Array.isArray(d.list) ? d.list : []
  return { items: list.map((r) => toItem(r)).filter((x): x is RfItem => !!x), hasMore: d.hasMore === true }
}

/** Appends a page, keeping the first copy of a row (the store's `new Set(ids)`). */
export function mergeItems(prev: RfItem[], next: RfItem[]): RfItem[] {
  const seen = new Set(prev.map((i) => `${i.type}:${i.id}`))
  return [...prev, ...next.filter((i) => !seen.has(`${i.type}:${i.id}`))]
}

/** `{list, hasMore, lastId}` of release-forms/mentions. */
export function parseMentions(data: unknown): { list: Raw[]; hasMore: boolean; lastId: string } {
  const d = obj(data)
  const list = Array.isArray(d.list) ? (d.list as unknown[]).map(obj) : []
  return { list, hasMore: d.hasMore === true, lastId: str(d.lastId) }
}

// ── Row rules (the release-form helpers) ─────────────────────────────────

export const isLinkForm = (type: string) => type === 'link'
export const isDocForm = (type: string) => type === 'document'
export const isTagForm = (type: string) => type === 'user'
export const isGuest = (i: RfItem) => !!i.token
export const isIdentityVerified = (i: RfItem) => !!i.user?.isIdentityVerified
export const isSigned = (i: RfItem) => isLinkForm(i.type) && (i.status === 'signed' || i.status === 'approved')
export const isViewed = (i: RfItem) => i.type === 'link' && i.status === 'viewed'
export const isHidden = (i: RfItem) => i.hidden || !!i.user?.hidden

/** "2026-10-20 10:00:00" (no zone) is UTC, as dayjs.utc reads it. */
export function parseUtc(date: string): number {
  if (!date) return NaN
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(date) ? date : `${date.replace(' ', 'T')}Z`
  return new Date(iso).getTime()
}

const DAY = 86_400_000
export const isLinkExpired = (i: RfItem, now = Date.now()) => !isSigned(i) && parseUtc(i.date) + 2 * DAY - now <= 0
export const isViewedExpired = (i: RfItem, now = Date.now()) => isLinkForm(i.type) && isViewed(i) && isLinkExpired(i, now)
export const showExpirationWarning = (i: RfItem, now = Date.now()) => !isSigned(i) && now > parseUtc(i.date) + DAY

/** Selection key: `user:1`, `guest:2`, `link:3`, `document:4`. */
export const selectKey = (i: RfItem, list: RfListTab) => `${list === 'users' ? (isGuest(i) ? 'guest' : 'user') : i.type}:${i.id}`

/** A row that cannot be picked (isRowDisabled): unsigned/expired/failed forms, unverified users, a full selection. */
export function isRowDisabled(i: RfItem, selected?: Set<string>, key?: string, now = Date.now()): boolean {
  const link = isLinkForm(i.type)
  return (
    (link && isLinkExpired(i, now) && !isSigned(i) && !isViewed(i)) ||
    i.status === 'failed' ||
    isViewedExpired(i, now) ||
    (link && !i.hasUser) ||
    (link && !isSigned(i)) ||
    (isTagForm(i.type) && !isIdentityVerified(i)) ||
    (!!selected && !!key && selected.size >= SELECT_MAX && !selected.has(key))
  )
}

export const showCopyLinkBtn = (i: RfItem, now = Date.now()) => isLinkForm(i.type) && !isSigned(i) && !isLinkExpired(i, now)
export const showEditBtn = (i: RfItem, now = Date.now()) => !isLinkExpired(i, now) || isDocForm(i.type)

// ── Copy texts (getCopyTexts, onlyfans.com as the origin) ───────────────────

export type CopyKind = 'guest' | 'profile' | 'username' | 'link'

export function copyText(kind: CopyKind, v: { token?: string; username?: string; code?: string; signature?: string }): { text: string; toast: string } {
  switch (kind) {
    case 'guest':
      return { text: `${OF}?rfguesttoken=${v.token ?? ''}`, toast: 'toast.copied' }
    case 'profile':
      return { text: `${OF}/${v.username ?? ''}`, toast: 'toast.profileCopied' }
    case 'username':
      return { text: `${v.username ?? ''}`, toast: 'toast.usernameCopied' }
    case 'link':
      return { text: linkUrl(v.code ?? '', v.signature ?? ''), toast: 'toast.copied' }
  }
}

/** The signing page a release form link opens. */
export const linkUrl = (code: string, signature: string) => `${OF}/my/release-forms/${code}?signature=${encodeURIComponent(signature)}`

// ── Text ────────────────────────────────────────────────────────────────────

/** The "99+" of the MENTIONS badge ('' for none). */
export function counter(n: unknown, max = 99): string {
  const v = Number(n)
  return !v || v <= 0 ? '' : v > max ? `${max}+` : `${v}`
}

/** OnlyFans stores names HTML-encoded (&amp; &quot; &lt; &gt; &reg;); its fields show them decoded. */
export function decodeEntities(s: string): string {
  return s.replace(/&(reg|amp|quot|lt|gt);/g, (_, k: string) => ({ reg: '®', amp: '&', quot: '"', lt: '<', gt: '>' })[k] ?? _)
}

/** Leading spaces do not count when comparing a new name with the old one. */
export const trimStart = (s: string) => s.replace(/^[ ]+/g, '')
/** Save is off for an empty or unchanged name. */
export const nameUnchanged = (next: string, prev: string) => !trimStart(next) || trimStart(next) === trimStart(prev)

export type FromNow = { key: 'seconds' | 'minute' | 'hour' | 'day' | 'month' | 'year' } | { key: 'minutes' | 'hours' | 'days' | 'months' | 'years'; count: number }

/** dayjs' `fromNow()` buckets ("a few seconds ago" … "3 years ago"). */
export function fromNow(date: string, now = Date.now()): FromNow | null {
  const t = parseUtc(date)
  if (Number.isNaN(t)) return null
  const s = Math.abs(now - t) / 1000
  const m = s / 60
  const h = m / 60
  const d = h / 24
  if (s < 45) return { key: 'seconds' }
  if (s < 90) return { key: 'minute' }
  if (m < 45) return { key: 'minutes', count: Math.round(m) }
  if (m < 90) return { key: 'hour' }
  if (h < 22) return { key: 'hours', count: Math.round(h) }
  if (h < 36) return { key: 'day' }
  if (d < 26) return { key: 'days', count: Math.round(d) }
  if (d < 46) return { key: 'month' }
  const months = d / 30.4
  if (months < 10.5) return { key: 'months', count: Math.round(months) }
  if (months < 18) return { key: 'year' }
  return { key: 'years', count: Math.round(d / 365) }
}

/** "Oct 20, 2026" (dayjs.utc(date).format('MMM D, YYYY')). */
export function formatDay(date: string, lang = 'en'): string {
  const t = parseUtc(date)
  if (Number.isNaN(t)) return ''
  return new Date(t).toLocaleDateString(lang, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

// ── This page's URL ─────────────────────────────────────────────────────────

export interface RfRoute {
  tab: RfTab
  /** OnlyFans' /my/release-forms?searchBy=users|forms: the search view. */
  searchBy: RfListTab | null
  q: string
}

const isListTab = (v: string | null): v is RfListTab => v === 'users' || v === 'forms'

export function parseRoute(query: string): RfRoute {
  const p = new URLSearchParams(query)
  const searchBy = p.get('searchBy')
  const type = p.get('type')
  const tab: RfTab = isListTab(searchBy) ? searchBy : type === 'forms' || type === 'mentions' ? type : 'users'
  return { tab, searchBy: isListTab(searchBy) ? searchBy : null, q: (p.get('q') ?? '').slice(0, SEARCH_MAX) }
}

export function buildRoute(r: { tab: RfTab; searchBy?: RfListTab | null }): string {
  if (r.searchBy) return `searchBy=${r.searchBy}`
  return r.tab === 'users' ? '' : `type=${r.tab}`
}
